// src/stations/sprint/sprintTimer.ts — 10-Minute Sprint Timer & Zen Mode (FR-QUEST-001)
// Designed for ADHD learners (Tudor): Zen Mode hides ticking clock numbers to prevent time-panic
// while keeping accurate background countdown.

export interface SprintTimerCallbacks {
  onTick?: (remainingSeconds: number, formattedTime: string) => void;
  onExpire?: () => void;
}

export class SprintTimer {
  private totalDuration: number;
  private remainingSeconds: number;
  private timerId: number | null = null;
  private zenMode: boolean = false;
  private running: boolean = false;
  private callbacks: SprintTimerCallbacks;

  constructor(
    durationSeconds: number = 600,
    callbacks: SprintTimerCallbacks = {}
  ) {
    this.totalDuration = Math.max(1, durationSeconds);
    this.remainingSeconds = this.totalDuration;
    this.callbacks = callbacks;

    // Load persisted Zen Mode preference from localStorage
    try {
      this.zenMode = localStorage.getItem('clawey_zen_mode') === 'true';
    } catch {
      this.zenMode = false;
    }
  }

  /** Start or resume countdown */
  start(): void {
    if (this.running) return;
    this.running = true;

    this.timerId = window.setInterval(() => {
      this.tick();
    }, 1000);

    // Initial notify
    this.callbacks.onTick?.(this.remainingSeconds, this.getFormattedTime());
  }

  /** Advance timer by 1 second */
  private tick(): void {
    if (!this.running) return;

    this.remainingSeconds -= 1;

    if (this.remainingSeconds <= 0) {
      this.remainingSeconds = 0;
      this.stop();
      this.callbacks.onTick?.(0, '00:00');
      this.callbacks.onExpire?.();
    } else {
      this.callbacks.onTick?.(this.remainingSeconds, this.getFormattedTime());
    }
  }

  /** Pause countdown */
  pause(): void {
    this.stop();
  }

  /** Resume countdown */
  resume(): void {
    this.start();
  }

  /** Stop interval without clearing progress */
  private stop(): void {
    this.running = false;
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  /** Reset timer back to duration */
  reset(newDurationSeconds?: number): void {
    this.stop();
    if (typeof newDurationSeconds === 'number' && newDurationSeconds > 0) {
      this.totalDuration = newDurationSeconds;
    }
    this.remainingSeconds = this.totalDuration;
    this.callbacks.onTick?.(this.remainingSeconds, this.getFormattedTime());
  }

  /** Toggle Zen Mode (hides visible timer display for ADHD anxiety reduction) */
  toggleZenMode(): boolean {
    this.zenMode = !this.zenMode;
    try {
      localStorage.setItem('clawey_zen_mode', String(this.zenMode));
    } catch {
      // Ignored in private browsing
    }
    return this.zenMode;
  }

  isZenMode(): boolean {
    return this.zenMode;
  }

  isRunning(): boolean {
    return this.running;
  }

  getRemaining(): number {
    return this.remainingSeconds;
  }

  getTotalDuration(): number {
    return this.totalDuration;
  }

  /** Returns formatted string "MM:SS" */
  getFormattedTime(): string {
    const mins = Math.floor(this.remainingSeconds / 60);
    const secs = this.remainingSeconds % 60;
    const mm = mins < 10 ? `0${mins}` : `${mins}`;
    const ss = secs < 10 ? `0${secs}` : `${secs}`;
    return `${mm}:${ss}`;
  }

  /** Cleanup resources */
  destroy(): void {
    this.stop();
  }
}
